import React from "react";
import tradingBg from '../assets/img/tradingbg2.png';

export default function FourthHome({ theme = "green", primaryColor }) {
    // Dynamic theme tokens matching dashboard state
    const primary = primaryColor || (theme === "green" ? "#10b981" : "#3b82f6");

    return (
        <div style={styles.wrapper}>
            <div style={styles.container}>
                {/* Section Header */}
                <header style={styles.sectionHeader}>
                    <h1 style={{ ...styles.companyTitle, color: primary }}>Our Company</h1>
                    <p style={styles.subtitle}>
                        Small team, big impact. Meet the people building products and supporting customers.
                    </p>
                </header>

                <main>
                    {/* Hero Section */}
                    <section style={styles.hero} aria-labelledby="hero-heading">
                        <div style={styles.heroCopy}>
                            <h2 id="hero-heading" style={styles.heroHeading}>
                                Innovation, Reliability, Service — Choose Us
                            </h2>
                            <p style={styles.heroText}>
                                We provide innovative, reliable solutions backed by exceptional service, 
                                empowering your business to grow confidently with modern technology, 
                                seamless performance, and dedicated support.
                            </p>

                            <div style={styles.actions}>
                                <button style={{ ...styles.btnPrimary, backgroundColor: primary }}>
                                    Get Started
                                </button>
                                <button style={{ ...styles.btnGhost, color: primary, borderColor: `${primary}35` }}>
                                    Learn More
                                </button>
                            </div>
                        </div>

                        <div style={styles.heroVisual}>
                            <div style={styles.imageCard}>
                                <div style={{ ...styles.glowOverlay, backgroundColor: primary }} />
                                <img 
                                    src={tradingBg} 
                                    alt="SwiftEx Trading Interface" 
                                    style={styles.heroImg} 
                                />
                            </div>
                        </div>
                    </section>

                    {/* Features Grid */}
                    <section style={styles.featuresGrid} aria-label="Key Features">
                        {/* Feature 1 */}
                        <div style={styles.featureCard}>
                            <div style={{ ...styles.iconBox, backgroundColor: `${primary}15`, color: primary }}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="4" r="2" stroke="currentColor" strokeWidth="1.8" />
                                    <circle cx="5" cy="18" r="2" stroke="currentColor" strokeWidth="1.8" />
                                    <circle cx="19" cy="18" r="2" stroke="currentColor" strokeWidth="1.8" />
                                    <path d="M12 6v4.5M12 10.5L6.5 15.5M12 10.5L17.5 15.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                </svg>
                            </div>
                            <div>
                                <h3 style={styles.featureTitle}>Scalable Architecture</h3>
                                <p style={styles.featureDesc}>Designed to grow: microservices-ready patterns and cloud-first infrastructure.</p>
                            </div>
                        </div>

                        {/* Feature 2 */}
                        <div style={styles.featureCard}>
                            <div style={{ ...styles.iconBox, backgroundColor: `${primary}15`, color: primary }}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 3v6l4 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                                </svg>
                            </div>
                            <div>
                                <h3 style={styles.featureTitle}>Reliable Performance</h3>
                                <p style={styles.featureDesc}>Fast load times and consistent uptime with optimized resources and caching.</p>
                            </div>
                        </div>

                        {/* Feature 3 */}
                        <div style={styles.featureCard}>
                            <div style={{ ...styles.iconBox, backgroundColor: `${primary}15`, color: primary }}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                    <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8" />
                                    <path d="M8 12h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                </svg>
                            </div>
                            <div>
                                <h3 style={styles.featureTitle}>Secure by Default</h3>
                                <p style={styles.featureDesc}>Built-in best practices for authentication, authorization, and data protection.</p>
                            </div>
                        </div>
                    </section>

                    {/* About Section */}
                    <section id="about">
                        <div style={styles.aboutCard}>
                            <h2 style={styles.aboutHeading}>About Our Company</h2>
                            <div style={styles.aboutContent}>
                                <p>
                                    At <strong>SwiftEx</strong>, we bring innovation and trust together in the world of cryptocurrency trading. 
                                    Our mission is to make digital asset trading simple, secure, and profitable for everyone — 
                                    from beginners to experienced investors.
                                </p>
                                <p>
                                    We operate through a network of professional trading agents, ensuring every client receives 
                                    personalized support and real-time guidance. Whether you’re looking to buy, sell, or expand 
                                    your crypto portfolio, our agents are here to help you make informed decisions based on 
                                    market insights and proven strategies.
                                </p>
                                <p>
                                    With cutting-edge technology, transparent operations, and a strong commitment to customer 
                                    success, we aim to create a seamless bridge between people and the fast-evolving world of 
                                    crypto finance.
                                </p>
                                <p style={{ fontWeight: 600, color: "#f8fafc", marginTop: 8 }}>
                                    At SwiftEx, trading isn’t just about numbers — it’s about partnership, expertise, and growth.
                                </p>
                            </div>
                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
}

const styles = {
    wrapper: {
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "transparent",
        color: "#f8fafc",
        fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
        display: "flex",
        justifyContent: "center",
        padding: "40px 0",
    },
    container: {
        width: "100%",
        maxWidth: 1200,
        padding: "0 24px",
        boxSizing: "border-box",
    },
    sectionHeader: {
        textAlign: "center",
        marginBottom: 48,
        backgroundColor: "transparent",
    },
    companyTitle: {
        fontSize: "clamp(2rem, 4vw, 2.75rem)",
        fontWeight: 800,
        margin: "0 0 12px 0",
        letterSpacing: "-0.02em",
        background: "transparent",
    },
    subtitle: {
        fontSize: 16,
        color: "#94a3b8",
        margin: 0,
        maxWidth: 580,
        marginLeft: "auto",
        marginRight: "auto",
        background: "transparent",
    },
    hero: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        gap: 40,
        alignItems: "center",
        marginBottom: 56,
    },
    heroCopy: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
    },
    heroHeading: {
        fontSize: "clamp(1.75rem, 3.5vw, 2.35rem)",
        fontWeight: 800,
        color: "#f8fafc",
        lineHeight: 1.15,
        margin: "0 0 16px 0",
        letterSpacing: "-0.01em",
    },
    heroText: {
        fontSize: 15,
        color: "#94a3b8",
        lineHeight: 1.65,
        margin: "0 0 28px 0",
    },
    actions: {
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
    },
    btnPrimary: {
        padding: "12px 24px",
        borderRadius: 10,
        border: "none",
        color: "#ffffff",
        fontWeight: 600,
        fontSize: 14,
        cursor: "pointer",
        transition: "transform 0.2s ease, opacity 0.2s ease",
        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
    },
    btnGhost: {
        padding: "12px 24px",
        borderRadius: 10,
        border: "1px solid",
        backgroundColor: "transparent",
        fontWeight: 600,
        fontSize: 14,
        cursor: "pointer",
        transition: "background-color 0.2s ease",
    },
    heroVisual: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    imageCard: {
        position: "relative",
        width: "100%",
        maxWidth: 520,
        backgroundColor: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(12px)",
        borderRadius: 20,
        padding: 16,
        border: "1px solid rgba(255, 255, 255, 0.08)",
        boxShadow: "0 20px 40px -15px rgba(0,0,0,0.5)",
        overflow: "hidden",
    },
    glowOverlay: {
        position: "absolute",
        top: "-20%",
        right: "-20%",
        width: "60%",
        height: "60%",
        borderRadius: "50%",
        filter: "blur(80px)",
        opacity: 0.15,
        pointerEvents: "none",
    },
    heroImg: {
        width: "100%",
        height: "auto",
        borderRadius: 12,
        display: "block",
        objectFit: "cover",
    },
    featuresGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: 20,
        marginBottom: 56,
    },
    featureCard: {
        display: "flex",
        gap: 16,
        alignItems: "flex-start",
        padding: 24,
        backgroundColor: "rgba(15, 23, 42, 0.5)",
        backdropFilter: "blur(12px)",
        borderRadius: 16,
        border: "1px solid rgba(255, 255, 255, 0.06)",
        boxShadow: "0 10px 25px -10px rgba(0, 0, 0, 0.3)",
    },
    iconBox: {
        width: 44,
        height: 44,
        borderRadius: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    featureTitle: {
        margin: "0 0 6px 0",
        fontSize: 16,
        fontWeight: 600,
        color: "#f1f5f9",
    },
    featureDesc: {
        margin: 0,
        fontSize: 14,
        color: "#94a3b8",
        lineHeight: 1.5,
    },
    aboutCard: {
        padding: "36px 32px",
        backgroundColor: "rgba(15, 23, 42, 0.5)",
        backdropFilter: "blur(12px)",
        borderRadius: 20,
        border: "1px solid rgba(255, 255, 255, 0.06)",
        boxShadow: "0 15px 35px -10px rgba(0, 0, 0, 0.3)",
    },
    aboutHeading: {
        margin: "0 0 20px 0",
        fontSize: "clamp(1.5rem, 3vw, 1.85rem)",
        fontWeight: 700,
        color: "#f8fafc",
    },
    aboutContent: {
        color: "#cbd5e1",
        fontSize: "clamp(0.95rem, 1.5vw, 1.05rem)",
        lineHeight: 1.7,
        display: "flex",
        flexDirection: "column",
        gap: 16,
    },
};